<script setup lang="ts">
import { useConfigStore } from '@core/stores/config'
import { useDisplay } from 'vuetify'
import PageTitle from '@/layouts/components/PageTitle.vue'
import Footer from '@/views/front-pages/front-page-footer.vue'
import Navbar from '@/views/front-pages/front-page-navbar.vue'

const store = useConfigStore()
const route = useRoute()
const { smAndDown } = useDisplay()

store.skin = 'default'

// Get page title data from route meta
interface PageTitleMeta {
  title?: string
  subtitle?: string
  icon?: string
  iconColor?: string
  compact?: boolean
  breadcrumbs?: unknown
}

const pageTitle = computed(() => (route.meta.pageTitle as PageTitleMeta) || {})

// Check if current page should be fullscreen on mobile (cart page)
const isFullscreenMobilePage = computed(() => {
  return smAndDown.value && route.name === 'cart'
})

// Determine active navigation item based on current route
const activeNavItem = computed(() => {
  const routeName = route.name as string

  // Contact page
  if (routeName === 'contact')
    return 'contact'

  // Ontdek 't SpeelFabrik pages
  if (routeName === 'over-speelfabrik' || routeName === 'ons-team' || routeName === 'word-monitor')
    return 'ontdek'

  // Check for hash-based sections on index page
  if (route.hash) {
    const hash = route.hash.replace('#', '')
    if (hash === 'home' || hash === 'monitoren' || hash === 'faq')
      return hash
  }

  // Default to home for index page without hash
  if (routeName === 'index')
    return 'home'

  return ''
})
</script>

<template>
  <div class="front-page-layout">
    <!-- Navigation Bar (hidden on mobile fullscreen pages) -->
    <Navbar v-if="!isFullscreenMobilePage" :active-id="activeNavItem" />
    <!-- Page Title Section (hidden on mobile fullscreen pages) -->
    <PageTitle
      v-if="!isFullscreenMobilePage && (pageTitle.title || $slots['page-title'])" :title="pageTitle.title"
      :subtitle="pageTitle.subtitle" :icon="pageTitle.icon" :icon-color="pageTitle.iconColor"
      :breadcrumbs="pageTitle.breadcrumbs" :compact="pageTitle.compact"
    >
      <slot name="page-title" />
    </PageTitle>

    <!-- Main Content Slot -->
    <slot />

    <!-- Footer (hidden on mobile fullscreen pages) -->
    <Footer v-if="!isFullscreenMobilePage" />
  </div>
</template>

<style lang="scss">
.front-page-layout {
  .v-container {
    background-color: transparent !important;
  }
}

@media (max-width: 960px) and (min-width: 600px) {
  .front-page-layout {
    .v-container {
      padding-inline: 2rem !important;
    }
  }
}
</style>
